import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { WorldMapMarkerDetailStyle } from '__generated__/globalTypes';
import { EditDetail } from 'components/detail/edit/EditDetail';
import { IConfirmActionModal } from 'components/site/modals/ModalWrapper/ModalContent/contents/ConfirmActionModal';
import { EModal } from 'components/site/modals/ModalWrapper/ModalContent/useGetModalProperties';
import { MinuteActionWord } from 'elements/minute/MinuteActionWord';
import { ActionButton } from 'elements/minute/sidebar/sidebarComponents/atomic/ActionButton';
import { useDetailCRUD } from 'hooks/detail/useDetailCRUD';
import { useWorldMarker } from 'hooks/marker/useWorldMarker';
import { EColors, EFonts } from 'libs/styles/styling';
import {
    getCardsFromCardsInput,
    getCardsInputFromCards,
    getDetailInputFromDetail
} from 'libs/utils/detail/detailInput';
import { useContext, useEffect, useRef } from 'react';
import { CtxDetail } from 'session/ctxDetail';
import { CtxEdit } from 'session/ctxEdit';
import { CtxModal } from 'session/ctxModal';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    detailId: string;
    className?: string;
}

export const EditInteractivity = (props: Props): JSX.Element => {
    const ctxDetail = useContext(CtxDetail);
    const ctxModal = useContext(CtxModal);
    const ctxEdit = useContext(CtxEdit);
    const isDetailLoaded =
        ctxDetail.selectedDetailId &&
        ctxDetail.updateDetailInput?.detailId === ctxDetail.selectedDetailId;
    const detailCRUD = useDetailCRUD(ctxDetail.map?.worldMapId);
    const worldMarker = useWorldMarker();
    const selectedMarker = worldMarker.getSingleMarkerIfOne();
    const ref = useRef(null);

    useEffect(() => {
        console.log('Render', props.detailId);
        if (props.detailId) {
            select(props.detailId);
        }
    }, [props.detailId]);

    useEffect(() => {
        if (isDetailLoaded) {
            if (ctxDetail.updateDetailInput?.cards.length === 0) {
                ctxDetail.setUpdatingIndex(-1);
            } else {
                ctxDetail.setUpdatingIndex(-2);
            }
        }
    }, [isDetailLoaded]);

    //detect click outside ref
    useEffect(() => {
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                if (ctxDetail.isNeedingToSave) {
                    ctxModal.setModal({
                        type: EModal.CONFIRM,
                        data: {
                            text: Localization.CONFIRM_SAVE,
                            onConfirm: async () => {
                                ctxDetail.save();
                            },
                            onCancel: async () => {
                                ctxDetail.reset();
                                ctxEdit.setMarkerSidebar('properties');
                            }
                        } as IConfirmActionModal
                    });
                }
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    });

    async function select(detailId: string) {
        console.log('Selecting detail', detailId);
        ctxDetail.reset();
        const query = await detailCRUD.readDetail(detailId);
        ctxDetail.setSelectedDetailId(detailId);
        const detail = query.data.moment;
        ctxDetail.changeDetailInput(
            {
                detailId: detail.storyId,
                detailInput: getDetailInputFromDetail(detail),
                cards: getCardsInputFromCards(detail.cards)
            },
            { triggerHistory: false }
        );
    }

    async function preview() {
        //construct detail from detailInput
        const cards = getCardsFromCardsInput(ctxDetail.updateDetailInput?.cards);
        ctxModal.setDetailModal({
            id: props.detailId,
            markerId: selectedMarker.id
        });
        const query = await detailCRUD.readDetail(props.detailId);
        const detail = query.data.moment;
        ctxModal.setDetailModal({
            id: props.detailId,
            detail: {
                ...detail,
                cards,
                header: { ...detail.header, ...ctxDetail.updateDetailInput?.detailInput.header }
            },
            markerId: selectedMarker.id
        });

        if (ctxEdit.map.markerDetailStyle === WorldMapMarkerDetailStyle.BUBBLE) {
            ctxEdit.mapRef.current.flyTo({
                center: {
                    lat: selectedMarker.lat,
                    lng: selectedMarker.lng
                },
                animate: true
            });
        }
    }

    async function remove() {
        ctxModal.setModal({
            type: EModal.CONFIRM,
            data: {
                text: Localization.CONFIRM_REMOVE_INTERACTIVITY,
                onConfirm: async () => {
                    ctxEdit.updateWorldMarker(selectedMarker.id, {
                        storyIds: []
                    });
                }
            } as IConfirmActionModal
        });
    }

    async function save() {
        ctxDetail.save();
    }

    if (!isDetailLoaded) {
        return (
            <S_LoadingBlock>
                <S_Loading icon={['far', 'spinner']} spin />
            </S_LoadingBlock>
        );
    }

    return (
        <S_EditInteractivity className={props.className} ref={ref}>
            <S_TopRow>
                {!selectedMarker.isExternalPoint && (
                    <S_Save>
                        {ctxDetail.isSaved && (
                            <>
                                <S_SavedIcon icon={['fal', 'check']} /> {Localization.ALL_SAVED}
                            </>
                        )}
                        {ctxDetail.saving && (
                            <>
                                <S_LoadingIcon icon={['fal', 'loader']} />
                                {Localization.SAVING}
                            </>
                        )}
                        {ctxDetail.isNeedingToSave && (
                            <S_SaveButton
                                icon={{ iconSet: 'fal', iconName: 'save' }}
                                onClick={save}
                            />
                        )}
                    </S_Save>
                )}
                <S_Actions>
                    <MinuteActionWord
                        onClick={preview}
                        title={
                            ctxModal.detailModal?.id
                                ? Localization.REFRESH_PREVIEW
                                : Localization.SHOW_PREVIEW
                        }
                    />
                    {!selectedMarker.isExternalPoint && (
                        <>
                            <>&nbsp;·&nbsp;</>
                            <MinuteActionWord
                                onClick={remove}
                                title={Localization.REMOVE_INTERACTIVITY}
                            />
                        </>
                    )}
                </S_Actions>
            </S_TopRow>
            <EditDetail key={ctxDetail.updateDetailInput.detailId} />
        </S_EditInteractivity>
    );
};

const S_EditInteractivity = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    padding: 0 0 100px 0;
`;

const S_TopRow = styled.div`
    display: flex;
    justify-content: space-between;
    height: 50px;
    align-items: center;
`;

const S_LoadingIcon = styled(FontAwesomeIcon)`
    @keyframes spin {
        100% {
            -webkit-transform: rotate(360deg);
            transform: rotate(360deg);
        }
    }

    animation: spin 4s linear infinite;
    margin-right: 6px;
`;
const S_SavedIcon = styled(FontAwesomeIcon)`
    margin-right: 6px;
`;

const S_SaveButton = styled(ActionButton)`
    font-size: 13px;
`;

const S_Actions = styled.div`
    display: flex;
    padding: 0 15px 12px 15px;
    justify-content: flex-end;
`;

const S_Save = styled.div`
    display: flex;
    padding: 0 15px 12px 15px;
    font-size: 13px;
    align-items: center;
    color: ${EColors.DARK};
    font-family: ${EFonts.Regular};
`;

const S_LoadingBlock = styled.div`
    display: flex;
    justify-content: center;
    margin: 20px 0 0 0;
`;

const S_Loading = styled(FontAwesomeIcon)`
    color: ${EColors.DARK};
    font-size: 30px;
`;
